import React, { useEffect, useMemo, useRef, useState } from "react";

/* ===============================
   CONSTANTS
================================ */
const IMPORTANCE = ["core", "supplementary", "administrative"];

const IMPORTANCE_LABEL = {
  core: "コア",
  supplementary: "補助用",
  administrative: "管理用",
};

const IMPORTANCE_TO_KEY = {
  コア: "core",
  補助用: "supplementary",
  管理用: "administrative",
};

const normalizeImportance = (value) =>
  IMPORTANCE_TO_KEY[value] ?? (IMPORTANCE.includes(value) ? value : "core");

const CONFIDENCE_ICON = {
  High: "✅",
  Medium: "⚠️",
  Low: "❌",
};

/* ===============================
   HELPERS
================================ */
const isObject = (v) => v && typeof v === "object" && !Array.isArray(v);
const isLeaf = (n) => isObject(n) && "値" in n;

const META_KEYS = new Set(["field_id", "key_jp"]);
const pathKey = (p) => p.join(" > ");
const splitPathKey = (k) => (k ? k.split(" > ").filter(Boolean) : []);

const normalizeOcrValue = (v) => {
  if (v == null) return "";
  if (typeof v !== "string") return v;
  const t = v.trim();
  if (t === "" || t === "null" || t === "チェックなし") return "";
  return t;
};

const isCheckboxKey = (k) =>
  k.includes("_銀行") ||
  k.includes("_信金") ||
  k.includes("_信組") ||
  k.includes("_農協") ||
  k.includes("_その他") ||
  k.includes("_普通") ||
  k.includes("_当座") ||
  k.includes("_貯蓄");

const cleanLabel = (k) => k.split("_").pop();

function sanitizeKeyName(name) {
  const n = (name || "").trim();
  if (!n) return null;
  if (META_KEYS.has(n)) return null;
  if (n.includes(" > ")) return null;
  return n;
}

function getNode(root, pathArr) {
  let node = root;
  for (const k of pathArr) {
    if (!isObject(node?.[k])) return null;
    node = node[k];
  }
  return node;
}

function getParentAndKey(root, pathArr) {
  if (!root || !pathArr?.length) return null;
  const parentPath = pathArr.slice(0, -1);
  const lastKey = pathArr[pathArr.length - 1];
  let parent = root;
  for (const k of parentPath) {
    if (!isObject(parent?.[k])) return null;
    parent = parent[k];
  }
  return { parent, lastKey };
}

function remapEditsKeys(prevEdits, oldPrefix, newPrefix) {
  const next = {};
  const oldP = oldPrefix;
  const oldWithSep = oldPrefix + " > ";
  for (const k of Object.keys(prevEdits || {})) {
    if (k === oldP) {
      next[newPrefix] = prevEdits[k];
    } else if (k.startsWith(oldWithSep)) {
      next[newPrefix + k.slice(oldP.length)] = prevEdits[k];
    } else {
      next[k] = prevEdits[k];
    }
  }
  return next;
}

function nextNewFieldName(sectionObj) {
  let i = 1;
  while (sectionObj[`新規項目${i}`] !== undefined) i++;
  return `新規項目${i}`;
}

/* ===============================
   MULTI-PAGE HELPERS
================================ */
function getPagesFromData(data) {
  // expected shape: { pages: [{page: 1, context: {...}}, ...] }
  if (data?.pages && Array.isArray(data.pages)) return data.pages;
  return null;
}

function getRenderableContext(data, selectedIdx) {
  const pages = getPagesFromData(data);
  if (!pages) return data;
  const item = pages[selectedIdx] || pages[0];
  return item?.context || {};
}

// prefix keys so edits don't collide between pages
function pagePrefix(pageNumber) {
  return `P${pageNumber}::`;
}
function withPagePrefix(prefix, key) {
  return `${prefix}${key}`;
}
function stripPagePrefix(prefix, key) {
  return key.startsWith(prefix) ? key.slice(prefix.length) : key;
}

/* ===============================
   COMPONENT
================================ */
export default function StreamlitTableRenderer({ data, setData, edits, setEdits }) {
  const tableRef = useRef(null);
  // Multi-page state
  const pages = useMemo(() => getPagesFromData(data), [data]);
  const [pageIdx, setPageIdx] = useState(0);

  // Keep selected page valid when data changes
  useEffect(() => {
    if (!pages) {
      setPageIdx(0);
      return;
    }
    if (pageIdx >= pages.length) setPageIdx(0);
  }, [pages, pageIdx]);

  const activePageNumber = pages ? (pages[pageIdx]?.page ?? pageIdx + 1) : 1;
  const activePrefix = pages ? pagePrefix(activePageNumber) : "";

  // This is what we actually render
  const activeData = useMemo(() => getRenderableContext(data, pageIdx), [data, pageIdx]);

  const [open, setOpen] = useState(new Set());
  const [editingKey, setEditingKey] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [addFolderDialog, setAddFolderDialog] = useState(null);
  useEffect(() => {
    const dialogOpen = Boolean(deleteConfirmation || addFolderDialog);
    if (!dialogOpen) return undefined;

    const scrollPanel = tableRef.current?.closest(".ocr-card");
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      panelOverflowY: scrollPanel?.style.overflowY || "",
      panelOverscroll: scrollPanel?.style.overscrollBehavior || "",
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };

    if (scrollPanel) {
      scrollPanel.style.overflowY = "hidden";
      scrollPanel.style.overscrollBehavior = "none";
    }
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      if (scrollPanel) {
        scrollPanel.style.overflowY = previous.panelOverflowY;
        scrollPanel.style.overscrollBehavior = previous.panelOverscroll;
      }
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.width = previous.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [deleteConfirmation, addFolderDialog]);

  const startEditLabel = (rowKeyStr, currentLabel) => {
    setEditingKey(rowKeyStr);
    setDraftLabel(currentLabel ?? "");
  };

  const cancelEditLabel = () => {
    setEditingKey(null);
    setDraftLabel("");
  };

  // NOTE: Renaming keys in multi-page is tricky because data is nested under pages[i].context.
  // For safety, we only enable rename when not multi-page OR when parent can be updated properly.
  const commitRenameKey = (rowKeyStr) => {
    if (typeof setData !== "function") return cancelEditLabel();

    const newName = sanitizeKeyName(draftLabel);
    if (!newName) return cancelEditLabel();

    // rowKeyStr here is WITHOUT prefix (we keep renderer internal keys unprefixed)
    const pathArr = splitPathKey(rowKeyStr);
    if (pathArr.length === 0) return cancelEditLabel();

    const oldName = pathArr[pathArr.length - 1];
    if (oldName === newName) return cancelEditLabel();

    const parentPath = pathArr.slice(0, -1);
    const parentKeyStr = pathKey(parentPath);
    const newKeyStr = parentPath.length ? `${parentKeyStr} > ${newName}` : newName;

    setData((prev) => {
      const next = structuredClone(prev);

      // choose root to edit (multi-page: pages[pageIdx].context)
      const targetRoot = pages
        ? (next.pages?.[pageIdx]?.context ?? null)
        : next;

      if (!targetRoot) return prev;

      const info = getParentAndKey(targetRoot, pathArr);
      if (!info) return prev;

      const { parent, lastKey } = info;
      if (!isObject(parent)) return prev;

      if (parent[newName] !== undefined) {
        alert("同じ名前の項目が既に存在します");
        return prev;
      }

      parent[newName] = parent[lastKey];
      delete parent[lastKey];
      return next;
    });

    // remap edits keys (with page prefix in multi-page)
    const oldEditsPrefixKey = pages ? withPagePrefix(activePrefix, rowKeyStr) : rowKeyStr;
    const newEditsPrefixKey = pages ? withPagePrefix(activePrefix, newKeyStr) : newKeyStr;

    setEdits((prevEdits) => remapEditsKeys(prevEdits, oldEditsPrefixKey, newEditsPrefixKey));

    setOpen((prevOpen) => {
      const nextOpen = new Set();
      const oldPrefix = rowKeyStr;
      const oldWithSep = rowKeyStr + " > ";
      for (const k of prevOpen) {
        if (k === oldPrefix) nextOpen.add(newKeyStr);
        else if (k.startsWith(oldWithSep)) nextOpen.add(newKeyStr + k.slice(oldPrefix.length));
        else nextOpen.add(k);
      }
      if (parentKeyStr) nextOpen.add(parentKeyStr);
      return nextOpen;
    });

    cancelEditLabel();
  };

  /* open all sections initially */
  useEffect(() => {
    const collect = (n, p = [], out = []) => {
      if (!isObject(n)) return;
      if (p.length) out.push(pathKey(p));
      Object.keys(n).forEach((k) => {
        if (!META_KEYS.has(k)) collect(n[k], [...p, k], out);
      });
      return out;
    };
    setOpen(new Set(collect(activeData) || []));
  }, [activeData]);

  /* ===============================
     STRUCTURE EDIT ACTIONS
================================ */

  const addSubFolder = (sectionKeyStr, requestedName = null) => {
    if (typeof setData !== "function") return;

    if (requestedName === null) {
      setAddFolderDialog({ parentKey: sectionKeyStr, name: "", error: "" });
      return;
    }

    const name = sanitizeKeyName(requestedName);
    if (!name) {
      setAddFolderDialog((prev) => ({ ...prev, error: "フォルダ名を入力してください。" }));
      return;
    }

    const currentParent = getNode(activeData, splitPathKey(sectionKeyStr));
    if (isObject(currentParent) && currentParent[name] !== undefined) {
      setAddFolderDialog((prev) => ({ ...prev, error: "同じ名前のフォルダが既に存在します。" }));
      return;
    }

    setData((prev) => {
      const next = structuredClone(prev);
      const targetRoot = pages ? (next.pages?.[pageIdx]?.context ?? null) : next;
      if (!targetRoot) return prev;

      const p = splitPathKey(sectionKeyStr);
      let node = targetRoot;
      for (const k of p) {
        if (!isObject(node[k])) node[k] = {};
        node = node[k];
      }

      node[name] = { 新規項目1: { 値: "", 重要度: "core" } };
      return next;
    });

    const childKey = `${sectionKeyStr} > ${name}`;
    setOpen((openKeys) => new Set([...openKeys, sectionKeyStr, childKey]));
    setAddFolderDialog(null);
  };
  const removeFolder = (sectionKeyStr, confirmed = false) => {
    if (typeof setData !== "function") return;

    const p = splitPathKey(sectionKeyStr);
    if (p.length === 0) return;

    if (!confirmed) {
      setDeleteConfirmation({ type: "folder", key: sectionKeyStr, name: p[p.length - 1] });
      return;
    }

    setData((prev) => {
      const next = structuredClone(prev);

      const targetRoot = pages
        ? (next.pages?.[pageIdx]?.context ?? null)
        : next;

      if (!targetRoot) return prev;

      const info = getParentAndKey(targetRoot, p);
      if (!info) return prev;
      const { parent, lastKey } = info;
      delete parent[lastKey];
      return next;
    });

    setOpen((s) => new Set([...s].filter((k) => k !== sectionKeyStr && !k.startsWith(sectionKeyStr + " > "))));

    const editsPrefix = pages ? activePrefix : "";
    setEdits((pEdits) => {
      const next = {};
      for (const k of Object.keys(pEdits || {})) {
        const kk = pages ? stripPagePrefix(editsPrefix, k) : k;
        if (kk === sectionKeyStr) continue;
        if (kk.startsWith(sectionKeyStr + " > ")) continue;
        next[k] = pEdits[k];
      }
      return next;
    });
  };

  const addEmptyField = (sectionKeyStr) => {
    if (typeof setData !== "function") return;

    setData((prev) => {
      const next = structuredClone(prev);

      const targetRoot = pages
        ? (next.pages?.[pageIdx]?.context ?? null)
        : next;

      if (!targetRoot) return prev;

      const node = getNode(targetRoot, splitPathKey(sectionKeyStr));
      if (!isObject(node)) return prev;

      const name = nextNewFieldName(node);
      node[name] = { 値: "", 重要度: "core" };
      return next;
    });

    setOpen((s) => new Set([...s, sectionKeyStr]));
  };

  const removeLeaf = (leafKeyStr, confirmed = false) => {
    if (typeof setData !== "function") return;

    const p = splitPathKey(leafKeyStr);
    if (p.length === 0) return;

    if (!confirmed) {
      setDeleteConfirmation({ type: "item", key: leafKeyStr, name: p[p.length - 1] });
      return;
    }

    setData((prev) => {
      const next = structuredClone(prev);

      const targetRoot = pages
        ? (next.pages?.[pageIdx]?.context ?? null)
        : next;

      if (!targetRoot) return prev;

      const info = getParentAndKey(targetRoot, p);
      if (!info) return prev;
      const { parent, lastKey } = info;
      delete parent[lastKey];
      return next;
    });

    const editsKey = pages ? withPagePrefix(activePrefix, leafKeyStr) : leafKeyStr;

    setEdits((prevEdits) => {
      const next = { ...(prevEdits || {}) };
      delete next[editsKey];
      return next;
    });
  };

  const setSectionImportance = (sectionKeyStr, importance) => {
    if (!IMPORTANCE.includes(importance)) return;
    const sectionNode = getNode(activeData, splitPathKey(sectionKeyStr));
    if (!isObject(sectionNode)) return;

    const descendantKeys = [];
    const collectLeaves = (node, path) => {
      if (!isObject(node)) return;
      if (isLeaf(node)) {
        descendantKeys.push(pathKey(path));
        return;
      }
      Object.keys(node)
        .filter((key) => !META_KEYS.has(key))
        .forEach((key) => collectLeaves(node[key], [...path, key]));
    };
    collectLeaves(sectionNode, splitPathKey(sectionKeyStr));

    setEdits((previous) => {
      const next = { ...(previous || {}) };
      descendantKeys.forEach((key) => {
        const editKey = pages ? withPagePrefix(activePrefix, key) : key;
        next[editKey] = { ...(next[editKey] || {}), importance };
      });
      return next;
    });
  };
  /* ===============================
     FLATTEN DATA
================================ */
  const rows = useMemo(() => {
    const out = [];

    const walk = (n, p = [], depth = 0) => {
      if (!isObject(n)) return;

      if (isLeaf(n)) {
        const k = pathKey(p);

        // edits key: prefixed for multi-page
        const ek = pages ? withPagePrefix(activePrefix, k) : k;
        const e = edits?.[ek] || {};

        const rawValue = e.value ?? n["値"];
        const value = normalizeOcrValue(rawValue);

        if (isCheckboxKey(p.at(-1)) && (rawValue === false || value === "")) return;

        const confidence = n["信頼度"];
        const confidenceLevel = confidence && typeof confidence === "object" ? confidence.level : null;
        const confidenceReason = confidence && typeof confidence === "object" ? confidence.reason || "" : "";

        out.push({
          type: "leaf",
          key: k,
          labelRaw: p.at(-1),
          label: n?.key_jp || (isCheckboxKey(p.at(-1)) ? cleanLabel(p.at(-1)) : p.at(-1)),
          depth,
          value:
            typeof rawValue === "boolean"
              ? rawValue
                ? "true"
                : ""
              : value,
          importance: normalizeImportance(e.importance ?? n["重要度"]),
          isCheckbox: typeof rawValue === "boolean",
          confidenceLevel,
          confidenceReason,
        });
        return;
      }

      if (p.length) {
        const sectionKey = pathKey(p);
        const descendantImportance = [];
        const collectImportance = (node, path) => {
          if (!isObject(node)) return;
          if (isLeaf(node)) {
            const leafKey = pathKey(path);
            const editKey = pages ? withPagePrefix(activePrefix, leafKey) : leafKey;
            descendantImportance.push(normalizeImportance(edits?.[editKey]?.importance ?? node["重要度"]));
            return;
          }
          Object.keys(node)
            .filter((key) => !META_KEYS.has(key))
            .forEach((key) => collectImportance(node[key], [...path, key]));
        };
        collectImportance(n, p);
        const uniqueImportance = [...new Set(descendantImportance)];

        out.push({
          type: "section",
          key: sectionKey,
          label: n?.key_jp || p.at(-1),
          depth,
          importance: uniqueImportance.length === 1 ? uniqueImportance[0] : "",
        });
        if (!open.has(sectionKey)) return;
      }

      Object.keys(n)
        .filter((k) => !META_KEYS.has(k))
        .forEach((k) => walk(n[k], [...p, k], p.length ? depth + 1 : 0));
    };

    walk(activeData);
    return out;
  }, [activeData, edits, open, pages, activePrefix]);

  /* ===============================
     RENDER
================================ */
  return (
    <div className="st-table" ref={tableRef}>
      {/* ✅ Multi-page selector */}
      {pages && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
          <span className="ocr-muted">Page:</span>
          <select
            className="st-select"
            value={pageIdx}
            onChange={(e) => {
              const idx = Number(e.target.value);
              setPageIdx(idx);
              setOpen(new Set()); // reset open; will re-open via effect
            }}
          >
            {pages.map((p, i) => (
              <option key={i} value={i}>
                Page {p.page}
              </option>
            ))}
          </select>
          <span className="ocr-muted">/ {pages.length}</span>
        </div>
      )}

      <div className="st-row st-head">
        <div>項目名</div>
        <div>抽出された値</div>
        <div>重要度</div>
      </div>

      {rows.map((r) =>
        r.type === "section" ? (
          <div className="st-row st-section" key={r.key}>
            <div
              style={{
                paddingLeft: r.depth * 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              <button
                className="st-toggle"
                onClick={() =>
                  setOpen((s) =>
                    new Set(s.has(r.key) ? [...s].filter((x) => x !== r.key) : [...s, r.key])
                  )
                }
                title={open.has(r.key) ? "閉じる" : "開く"}
              >
                {open.has(r.key) ? "▾" : "▸"}
              </button>

              <span style={{ lineHeight: 1 }}>📁</span>

              {editingKey === r.key ? (
                <input
                  className="st-input"
                  value={draftLabel}
                  autoFocus
                  onChange={(e) => setDraftLabel(e.target.value)}
                  onBlur={() => commitRenameKey(r.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRenameKey(r.key);
                    if (e.key === "Escape") cancelEditLabel();
                  }}
                  style={{ width: 280, maxWidth: 420 }}
                />
              ) : (
                <span
                  onDoubleClick={() => startEditLabel(r.key, r.label)}
                  title="ダブルクリックで編集"
                  style={{ cursor: "text" }}
                >
                  {r.label}
                </span>
              )}

              <div style={{ display: "inline-flex", gap: 6, marginLeft: 8 }}>
                <button
                  type="button"
                  className="ocr-btn ghost"
                  onClick={() => addSubFolder(r.key)}
                  title="サブフォルダを追加"
                  disabled={typeof setData !== "function"}
                  style={{ height: 26, padding: "0 10px", borderRadius: 8, lineHeight: 1 }}
                >
                  +
                </button>

                <button
                  type="button"
                  className="ocr-btn ghost"
                  onClick={() => removeFolder(r.key)}
                  title="フォルダを削除"
                  disabled={typeof setData !== "function"}
                  style={{ height: 26, padding: "0 10px", borderRadius: 8, lineHeight: 1 }}
                >
                  −
                </button>

                <button
                  type="button"
                  className="ocr-btn ghost"
                  onClick={() => addEmptyField(r.key)}
                  title="空の項目行を追加"
                  disabled={typeof setData !== "function"}
                  style={{ height: 26, padding: "0 10px", borderRadius: 8, lineHeight: 1 }}
                >
                  +行
                </button>
              </div>
            </div>

            <div />
            <div>
              <select
                className="st-select"
                value={r.importance}
                aria-label={`${r.label} の重要度を一括変更`}
                onChange={(event) => setSectionImportance(r.key, event.target.value)}
              >
                <option value="" disabled>一括変更</option>
                {IMPORTANCE.map((importance) => (
                  <option key={importance} value={importance}>{IMPORTANCE_LABEL[importance]}</option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="st-row" key={r.key}>
            <div
              style={{
                paddingLeft: r.depth * 14,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {r.confidenceLevel && CONFIDENCE_ICON[r.confidenceLevel] && (
                <span className="confidence-icon" title={r.confidenceReason}>
                  {CONFIDENCE_ICON[r.confidenceLevel]}
                </span>
              )}

              {editingKey === r.key ? (
                <input
                  className="st-input"
                  value={draftLabel}
                  autoFocus
                  onChange={(e) => setDraftLabel(e.target.value)}
                  onBlur={() => commitRenameKey(r.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRenameKey(r.key);
                    if (e.key === "Escape") cancelEditLabel();
                  }}
                  style={{ width: 220 }}
                />
              ) : (
                <span
                  onDoubleClick={() => startEditLabel(r.key, r.labelRaw)}
                  title="ダブルクリックで項目名を編集"
                  style={{ cursor: "text" }}
                >
                  {r.label}
                </span>
              )}

              <button
                type="button"
                className="ocr-btn ghost"
                onClick={() => removeLeaf(r.key)}
                title="この項目を削除"
                disabled={typeof setData !== "function"}
                style={{
                  height: 22,
                  padding: "0 8px",
                  borderRadius: 8,
                  lineHeight: 1,
                  marginLeft: 6,
                }}
              >
                −
              </button>
            </div>

            <div>
              {r.isCheckbox ? (
                <input className="st-input" value="✓" readOnly />
              ) : (
                <input
                  className="st-input"
                  value={r.value}
                  onChange={(e) => {
                    const v = e.target.value;

                    const keyBase = r.key;
                    const ek = pages ? withPagePrefix(activePrefix, keyBase) : keyBase;

                    setEdits((p) => ({
                      ...(p || {}),
                      [ek]: {
                        ...((p && p[ek]) || {}),
                        value: v,
                      },
                    }));
                  }}
                />
              )}
            </div>

            <div>
              <select
                className="st-select"
                value={r.importance}
                onChange={(e) => {
                  const v = e.target.value;

                  const keyBase = r.key;
                  const ek = pages ? withPagePrefix(activePrefix, keyBase) : keyBase;

                  setEdits((p) => ({
                    ...(p || {}),
                    [ek]: {
                      ...((p && p[ek]) || {}),
                      importance: v,
                    },
                  }));
                }}
              >
                {IMPORTANCE.map((x) => (
                  <option key={x} value={x}>
                    {IMPORTANCE_LABEL[x]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )
      )}

      {addFolderDialog && (
        <div
          className="ocr-confirm-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setAddFolderDialog(null);
          }}
        >
          <form
            className="ocr-confirm-dialog ocr-add-folder-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-folder-dialog-title"
            onSubmit={(event) => {
              event.preventDefault();
              addSubFolder(addFolderDialog.parentKey, addFolderDialog.name);
            }}
          >
            <div className="ocr-confirm-icon ocr-confirm-icon--add" aria-hidden="true">+</div>
            <div className="ocr-confirm-content">
              <h3 id="add-folder-dialog-title">フォルダを追加</h3>
              <p>新しいフォルダ名を入力してください。</p>
              <input
                className="ocr-confirm-input"
                type="text"
                value={addFolderDialog.name}
                maxLength={120}
                autoFocus
                placeholder="例：追加情報"
                onChange={(event) => {
                  const nextName = event.target.value;
                  setAddFolderDialog((prev) => ({ ...prev, name: nextName, error: "" }));
                }}
              />
              {addFolderDialog.error && <span className="ocr-confirm-error">{addFolderDialog.error}</span>}
            </div>
            <div className="ocr-confirm-actions">
              <button type="button" className="ocr-confirm-cancel" onClick={() => setAddFolderDialog(null)}>
                キャンセル
              </button>
              <button type="submit" className="ocr-confirm-add" disabled={!addFolderDialog.name.trim()}>
                追加する
              </button>
            </div>
          </form>
        </div>
      )}
      {deleteConfirmation && (
        <div
          className="ocr-confirm-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDeleteConfirmation(null);
          }}
        >
          <div className="ocr-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-dialog-title">
            <div className="ocr-confirm-icon" aria-hidden="true">!</div>
            <div className="ocr-confirm-content">
              <h3 id="delete-dialog-title">{deleteConfirmation.type === "folder" ? "フォルダを削除" : "項目を削除"}</h3>
              <p>「{deleteConfirmation.name}」を削除します。この操作は元に戻せません。</p>
            </div>
            <div className="ocr-confirm-actions">
              <button type="button" className="ocr-confirm-cancel" onClick={() => setDeleteConfirmation(null)}>
                キャンセル
              </button>
              <button
                type="button"
                className="ocr-confirm-delete"
                autoFocus
                onClick={() => {
                  const pending = deleteConfirmation;
                  setDeleteConfirmation(null);
                  if (pending.type === "folder") removeFolder(pending.key, true);
                  else removeLeaf(pending.key, true);
                }}
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
